/* jshint esversion: 6 */
/* globals angular, $objectCache, ITEMS_PER_GROUP, DECIMAL_PRECISION, isCMS, QuestionPart, Sheet, QuestionItemGroup */

/**
 * Represents a Task with various properties and methods to manage sheets, question parts, and marks.
 */
import QuestionPart from './QuestionPart';
import Sheet from './Sheet';
import QuestionItemGroup from './QuestionItemGroup';
import RepositoryEntry from './RepositoryEntry';

class Task extends RepositoryEntry {

    static DECIMAL_PRECISION   = 4;
    static ITEMS_PER_GROUP     = 999;

    /**
     * Constructs a new Task instance.
     * @param {Object} entry - The initial data for the task.
     */
    constructor(entry, $injector, $objectCache) {
        console.log('Task constructor', entry, $injector);
        super(entry, $injector);
        this.init();                
        this.$objectCache = $objectCache;
    }

    /**
     * Initializes the task with default values and configurations.
     * @returns {Task} The current Task instance.
     */
    init() {
        //this.objid = Math.random();

        this.accessables({
            'maxTime': Number
        });

        this.readables({
            'candidate': Object,
            'backgroundResourceSheetTeaserOnly': Boolean,
            'type': String
        });

        this.references({
            'questionParts': QuestionPart,
            'sheets': Sheet,
            'questionItemGroups': QuestionItemGroup,
            'iframeTools': '*'
        });

        /*this.validators({
            'MinMarksValidator': 'MinMarksValidator',
            'MaxMarksValidator': 'MaxMarksValidator',
            'OrderSheetValidator': 'OrderSheetValidator'
        });*/

        this.initPreReleaseJournal();

        return this;
    }

    /**
     * Initializes the pre-release and journal objects.
     */
    initPreReleaseJournal() {
        this.journalObj = null;
        this.preReleaseObj = null;
        this.sheets = {};
        angular.forEach(this.getSheets(), (value, index) => {
            if (value.isJournal()) {
                this.journalObj = value;
            } else if (value.isPreRelease()) {
                this.preReleaseObj = value;
            } else if (!value.isJournal() && !value.isPreRelease()) {
                this.sheets[index] = value;
            }
        });
    }

    /**
     * Gets the sheets without pre-release and journal.
     * @returns {Object} The sheets without pre-release and journal.
     */
    getSheetsWithoutPreReleaseAndJournal() {
        return this.sheets;
    }

    /**
     * Gets the pre-release object.
     * @returns {Object|null} The pre-release object or null if not present.
     */
    getPreRelease() {
        return this.preReleaseObj;
    }

    /**
     * Gets the journal object.
     * @returns {Object|null} The journal object or null if not present.
     */
    getJournal() {
        return this.journalObj;
    }

    /**
     * Checks if there is a pre-release object.
     * @returns {boolean} True if there is a pre-release object, false otherwise.
     */
    hasPreRelease() {
        return this.preReleaseObj !== null;
    }

    /**
     * Checks if there is a journal object.
     * @returns {boolean} True if there is a journal object, false otherwise.
     */
    hasJournal() {
        return this.journalObj !== null;
    }

    /**
     * Groups sheets into manageable chunks.
     * @returns {Array} The grouped sheets.
     */
    getGroupedSheets() {
        const sheets = this.getSheetsWithoutPreReleaseAndJournal();
        const groupUUID = this.getUUID() + 'sheet-group';
        let groupedSheets = this.$objectCache.get(groupUUID);
        const itemsPerGroup = Task.ITEMS_PER_GROUP;

        if (groupedSheets === undefined || isCMS) {
            groupedSheets = [];
            groupedSheets.uuid = groupUUID;

            let idx = 0;
            let currentGroup = [];
            angular.forEach(sheets, (sheet) => {
                if (idx % itemsPerGroup === 0) {
                    if (idx > 0) {
                        groupedSheets.push(currentGroup);
                    }
                    currentGroup = [];

                    currentGroup.getGroupTitle = function () {
                        let title = this[0].getShortTitle();
                        if (this.length > 1) {
                            title += ' - ';
                            title += this[this.length - 1].getShortTitle();
                        }
                        return title;
                    };

                    currentGroup.getStatusString = function () {
                        let status = 0;
                        let counter = 0;
                        let result = 'not-started';
                        let resultNumber = 0;

                        for (let gi in this) {
                            if (this[gi] instanceof Object && typeof this[gi].getTotalProgress === 'function') {
                                status += this[gi].getTotalProgress();
                                counter += 1;
                            }
                        }

                        resultNumber = status / counter;
                        if (resultNumber === 1) {
                            result = 'completed';
                        } else if (resultNumber === 0) {
                            result = 'not-started';
                        } else if (resultNumber > 0 && resultNumber < 1) {
                            result = 'partly-completed';
                        }
                        return result;
                    };
                }
                currentGroup.push(sheet);
                idx++;
            });
            groupedSheets.push(currentGroup);

            this.$objectCache.put(groupedSheets);
        }

        return groupedSheets;
    }

    /**
     * Gets all bookmarks from the sheets.
     * @returns {Array} The bookmarks.
     */
    getBookmarks() {
        let bookmarks = [];
        const sheets = this.getSheets();

        for (let idx = 0; idx < sheets.length; idx++) {
            bookmarks = bookmarks.concat(sheets[idx].getBookmarks());
        }

        return bookmarks;
    }

    /**
     * Calculates the total marks from all sheets.
     * @returns {number} The total marks.
     */
    getTotalMarks() {
        let totalMarks = 0;
        const sheets = this.getSheets();

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                totalMarks += sheets[si].getTotalMarks();
            }
        }

        return totalMarks;
    }

    /**
     * Calculates the total marks from sheets that have been started.
     * @returns {number} The total started marks.
     */
    getTotalStartedMarks() {
        let totalMarks = 0;
        const sheets = this.getSheets();

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                totalMarks += sheets[si].getTotalStartedMarks();
            }
        }

        return totalMarks;
    }

    /**
     * Calculates the total marks from sheets that have not been started.
     * @returns {number} The total not started marks.
     */
    getTotalNotStartedMarks() {
        let totalMarks = 0;
        const sheets = this.getSheets();

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                totalMarks += sheets[si].getTotalNotStartedMarks();
            }
        }

        return totalMarks;
    }

    /**
     * Calculates the total marks from sheets that have been finished.
     * @returns {number} The total finished marks.
     */
    getTotalFinishedMarks() {
        let totalMarks = 0;
        const sheets = this.getSheets();

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                totalMarks += sheets[si].getTotalFinishedMarks();
            }
        }

        return totalMarks;
    }

    /**
     * Calculates the percentage of started marks.
     * @returns {string} The percentage of started marks.
     */
    getPercentageStartedMarks() {
        const percentage = (this.getTotalStartedMarks() / this.getTotalMarks()) * 100;
        return percentage.toFixed(Task.DECIMAL_PRECISION);
    }

    /**
     * Calculates the percentage of not started marks.
     * @returns {string} The percentage of not started marks.
     */
    getPercentageNotStartedMarks() {
        const percentage = (this.getTotalNotStartedMarks() / this.getTotalMarks()) * 100;
        return percentage.toFixed(Task.DECIMAL_PRECISION);
    }

    /**
     * Calculates the percentage of finished marks.
     * @returns {string} The percentage of finished marks.
     */
    getPercentageFinishedMarks() {
        const percentage = (this.getTotalFinishedMarks() / this.getTotalMarks()) * 100;
        return percentage.toFixed(Task.DECIMAL_PRECISION);
    }

    /**
     * Calculates the total marks in percentage.
     * @returns {Object} The total marks in percentage.
     */
    getTotalMarksInPercentage() {
        const onehundred = 100;
        const initValues = [
            this.getPercentageFinishedMarks(),
            this.getPercentageStartedMarks(),
            this.getPercentageNotStartedMarks()
        ];

        const roundedValues = initValues.map(a => Math.floor(a));
        const sumValues = roundedValues.reduce((a, b) => a + b);
        const biggestValue = roundedValues.reduce((a, b) => a > b ? a : b);

        if (sumValues < onehundred) {
            for (let ri in roundedValues) {
                if (roundedValues.hasOwnProperty(ri) && roundedValues[ri] === biggestValue) {
                    roundedValues[ri] += onehundred - sumValues;
                    break;
                }
            }
        } else if (sumValues > onehundred) {
            for (let ri in roundedValues) {
                if (roundedValues.hasOwnProperty(ri) && roundedValues[ri] === biggestValue) {
                    roundedValues[ri] -= sumValues - onehundred;
                    break;
                }
            }
        }

        return {
            'finishedMarks': roundedValues[0],
            'startedMarks': roundedValues[1],
            'notStartedMarks': roundedValues[2]
        };
    }

    /**
     * Gets the sheets that have been finished.
     * @returns {Array} The finished sheets.
     */
    getFinishedSheets() {
        const sheets = this.getSheets();
        const finishedSheets = [];

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                const questionParts = sheets[si].getQuestionParts();
                const questionsLength = questionParts.length;

                let counter = 0;
                for (let i = 0; i < questionParts.length; i++) {
                    if (questionParts[i].getStatus() !== 0) {
                        counter++;
                    }
                }
                if (counter === questionsLength) {
                    finishedSheets.push(sheets[si]);
                }
            }
        }

        return finishedSheets;
    }

    /**
     * Gets the sheets that have been started.
     * @returns {Array} The started sheets.
     */
    getStartedSheets() {
        const sheets = this.getSheets();
        const startedSheets = [];

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                const questionParts = sheets[si].getQuestionParts();
                const questionsLength = questionParts.length;

                let counter = 0;
                for (let i = 0; i < questionParts.length; i++) {
                    if (questionParts[i].getStatus() !== 0) {
                        counter++;
                    }
                }
                if (counter > 0 && counter !== questionsLength) {
                    startedSheets.push(sheets[si]);
                }
            }
        }

        return startedSheets;
    }

    /**
     * Gets the sheets that have not been started.
     * @returns {Array} The not started sheets.
     */
    getNotStartedSheets() {
        const sheets = this.getSheets();
        const notStartedSheets = [];

        for (let si in sheets) {
            if (sheets.hasOwnProperty(si)) {
                const questionParts = sheets[si].getQuestionParts();
                const questionsLength = questionParts.length;
                const questionStatusSum = this.getSheetStatusSum(sheets[si]);

                if (questionStatusSum / questionsLength === 0) {
                    notStartedSheets.push(sheets[si]);
                }
            }
        }

        return notStartedSheets;
    }

    /**
     * Finds the sheet containing a specific question part.
     * @param {Object} questionPart - The question part to find.
     * @returns {Object|undefined} The sheet containing the question part.
     */
    findSheetWithQuestionPart(questionPart) {
        const sheets = this.getSheets();
        let result;

        for (let idx = 0; idx < sheets.length; idx++) {
            if (sheets[idx].hasQuestionPart(questionPart)) {
                result = sheets[idx];
                break;
            }
        }

        return result;
    }

    /**
     * Calculates the sum of statuses for question parts in a sheet.
     * @param {Object} sheet - The sheet to calculate the status sum for.
     * @returns {number} The sum of statuses.
     */
    getSheetStatusSum(sheet) {
        const questionParts = sheet.getQuestionParts();
        let statusSum = 0;

        for (let qi in questionParts) {
            if (questionParts.hasOwnProperty(qi)) {
                statusSum += questionParts[qi].getStatus();
            }
        }
        return statusSum;
    }

    /**
     * Gets the length of a group.
     * @returns {number} The length of a group.
     */
    getGroupLength() {
        return ITEMS_PER_GROUP;
    }

    /**
     * Gets the UUID of the task.
     * @returns {string} The UUID of the task.
    getUUID() {
        return this.objid;
    }
    */

    /**
     * Gets the sheets of the task.
     * @returns {Array} The sheets of the task.
     */
    getSheets() {
        return Object.values(this.sheets);
    }
}

export default Task;